import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import {
  WS_MESSAGE_TYPE,
  useWsReconnect,
  useWsSubscription,
} from '#/shared/lib/ws'

import { invalidateCampaignQueries } from '../lib/invalidate'

/**
 * A running campaign emits a frame per campaign per runner beat, and a beat
 * that sends a step, books an order and takes a reply emits three inside a
 * second. Collapsing them into one refetch is the difference between one
 * round of requests and several.
 */
const REFETCH_DEBOUNCE_MS = 400

/**
 * Keeps a campaign screen live without polling.
 *
 * `campaign_updated` is deliberately coarse: it names the campaign and nothing
 * else, so the only correct response is to drop the campaign queries and let
 * the mounted screens refetch. That covers the waiting-on-us counters, step
 * sends and the activity feed — the three things that move on their own.
 *
 * The frame is broadcast to the whole org, so a campaign the viewer is not
 * looking at still lands here; `invalidateCampaignQueries` drops the lot
 * either way, which is cheaper than matching ids to decide.
 */
export function useCampaignRealtime(): void {
  const queryClient = useQueryClient()
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const refetch = useCallback(() => {
    invalidateCampaignQueries(queryClient)
  }, [queryClient])

  const schedule = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(refetch, REFETCH_DEBOUNCE_MS)
  }, [refetch])

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  useWsSubscription(WS_MESSAGE_TYPE.campaignUpdated, schedule)
  // Frames were missed while the socket was down — go straight through.
  useWsReconnect(refetch)
}
